import BaseApi from '@lumapps/base-api';
import type { QueryClient } from '@lumapps/base-api/react-query';
import type { ServerListResponse } from '@lumapps/base-api/types';
import { createGetMultiBatcher } from '@lumapps/base-api/utils/createGetMultiBatcher';
import { ONE_MINUTE } from '@lumapps/utils/time/constants';
import type { Fields } from '@lumapps/utils/types/Fields';

import type { CustomContentType } from '../types';

const MAX_RESULTS = 100;

export const contentApi = new BaseApi({ path: 'customcontenttype' });

export interface ListAPIParams {
    /** instance id */
    instance: string | string[];
    /** how many results we want to retrieve */
    maxResults?: number;
    /** current language */
    lang?: string;
    /** whether cache should not be considered */
    invalidateCache?: boolean;
    /**  the pattern used to filter the list by name */
    name?: string;
    /**  the list of fields that will be returned with the response */
    fields?: Fields<CustomContentType>;
    /** Whether the response should include the sibling instances CCTs */
    includeInstanceSiblings?: boolean;
    /**
     * Whether to show the heritable CCTs or not
     * @default 'all'
     */
    showHeritable?: 'only-heritable' | 'non-heritable' | 'all';
}

/** Retrieves the list of custom content types for the current instance */
export const list = async (
    { fields: queryFields, invalidateCache, showHeritable = 'all', ...baseParams }: ListAPIParams,
    signal: AbortSignal | undefined = undefined,
) => {
    const params: any = { maxResults: MAX_RESULTS, ...baseParams };
    /**
     * If this API is called with invalidateCache set to true, the idea is to avoid the caching policies
     * set by the backend so that we have the latest data available. In order to do so, we just add a
     * timestamp parameter that will make each request unique, therefore invalidating the cache on the backend side
     * and retrieving the latest data
     */
    if (invalidateCache) {
        params.t = Date.now();
    }

    if (queryFields?.length) {
        params.fields = `items(${queryFields.join(', ')})`;
    }

    const res = await contentApi.get<ServerListResponse<CustomContentType>>('list', { params, signal });

    // Manual heritable filter
    if (showHeritable === 'only-heritable') {
        res.data.items = res.data.items.filter((contentType) => contentType.heritable);
    } else if (showHeritable === 'non-heritable') {
        res.data.items = res.data.items.filter((contentType) => !contentType.heritable);
    }

    return res;
};

export interface GetMultiParams {
    uids: string[];
    fields?: string;
}

/** @deprecated please use `getContentTypeBatched` instead */
export const getMulti = (params: GetMultiParams, signal?: AbortSignal) =>
    contentApi.get<ServerListResponse<CustomContentType>>('getMulti', { params, signal });

export const deleteMulti = (params: { uid: string[] }) => contentApi.delete('deleteMulti', { params });

export const save = (params: any) => contentApi.post<any>('save', params);

/** Get content type batched */
export const getContentTypeBatched = createGetMultiBatcher<CustomContentType>({
    getId: 'id',
    async getMulti(uids, fieldsArray, signal) {
        const fields = fieldsArray?.length ? `items(${fieldsArray.join(',')})` : undefined;
        // eslint-disable-next-line deprecation/deprecation
        const { data } = await getMulti({ uids, fields }, signal);
        return data.items;
    },
});

export const contentTypesQueryKeys = {
    all: () => ['content-types'],
    get: (id?: string, fields?: Fields<CustomContentType>) => [...contentTypesQueryKeys.all(), 'get', id, fields],
    list: (params: ListAPIParams) => [...contentTypesQueryKeys.all(), 'list', params],
};

/** Get query content type list */
export function getQueryContentTypeList(queryClient: QueryClient, params: ListAPIParams, enabled: boolean) {
    return {
        queryKey: contentTypesQueryKeys.list(params),
        queryFn: async ({ signal }: { signal?: AbortSignal }) => {
            const {
                data: { items },
            } = await list(params, signal);
            for (const item of items) {
                queryClient.setQueryData(contentTypesQueryKeys.get(item.id, params.fields), item);
            }
            return items;
        },
        staleTime: 5 * ONE_MINUTE,
        enabled,
    } as const;
}

/** Get query content type */
export const getQueryContentType = (id?: string, fields: Fields<CustomContentType> = []) =>
    ({
        queryKey: contentTypesQueryKeys.get(id, fields),
        queryFn: async ({ signal }: { signal?: AbortSignal }) => getContentTypeBatched(id as string, fields, signal),
        staleTime: 5 * ONE_MINUTE,
        enabled: Boolean(id),
    }) as const;
